import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Star, Battery, BatteryLow, BatteryMedium, BatteryFull, BatteryCharging, ChevronRight } from 'lucide-react';

const ENERGY_LEVELS = [
  { value: 1, icon: BatteryLow, label_fr: 'Vide', label_en: 'Empty', color: 'text-red-500' },
  { value: 2, icon: BatteryLow, label_fr: 'Faible', label_en: 'Low', color: 'text-orange-500' },
  { value: 3, icon: BatteryMedium, label_fr: 'Correct', label_en: 'OK', color: 'text-yellow-500' },
  { value: 4, icon: BatteryFull, label_fr: 'Bon', label_en: 'Good', color: 'text-green-500' },
  { value: 5, icon: BatteryCharging, label_fr: 'Pleine', label_en: 'Full', color: 'text-primary' },
];

export default function WorkoutDebriefForm({ language, onSubmit }) {
  const [intensity, setIntensity] = useState(0);
  const [energy, setEnergy] = useState(0);
  const [hoveredStar, setHoveredStar] = useState(0);
  const fr = language === 'fr';

  const canSubmit = intensity > 0 && energy > 0;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="workout-debrief-form">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />

      <div className="relative w-full max-w-sm bg-card rounded-2xl border border-border overflow-hidden animate-in zoom-in-95 fade-in duration-400">
        {/* Header */}
        <div className="px-6 pt-6 pb-2 text-center">
          <p className="text-[10px] uppercase tracking-[4px] text-muted-foreground mb-1">
            {fr ? 'rapport de fin de mission' : 'end of mission report'}
          </p>
          <h2 className="font-heading text-xl font-black uppercase tracking-tight text-foreground">
            {fr ? 'Debriefing' : 'Debrief'}
          </h2>
        </div>

        {/* Intensity Rating (Stars) */}
        <div className="px-6 py-4 space-y-2">
          <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
            {fr ? "Intensite de ta séance" : "Session intensity"}
          </label>
          <div className="flex items-center justify-center gap-2" data-testid="intensity-stars">
            {[1, 2, 3, 4, 5].map(val => (
              <button
                key={val}
                type="button"
                onClick={() => setIntensity(val)}
                onMouseEnter={() => setHoveredStar(val)}
                onMouseLeave={() => setHoveredStar(0)}
                className="p-1 transition-transform hover:scale-110 active:scale-95"
                data-testid={`intensity-star-${val}`}
              >
                <Star
                  className={`w-9 h-9 transition-colors ${
                    val <= (hoveredStar || intensity)
                      ? 'text-amber-400 fill-amber-400'
                      : 'text-zinc-700'
                  }`}
                />
              </button>
            ))}
          </div>
          {intensity > 0 && (
            <p className="text-center text-xs text-amber-400/80 font-medium">
              {intensity}/5
            </p>
          )}
        </div>

        {/* Separator */}
        <div className="mx-6 border-t border-border" />

        {/* Energy Level (Batteries) */}
        <div className="px-6 py-4 space-y-2">
          <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
            {fr ? "Niveau d'energie actuel" : "Current energy level"}
          </label>
          <div className="flex items-center justify-center gap-1.5" data-testid="energy-batteries">
            {ENERGY_LEVELS.map(lvl => {
              const Icon = lvl.icon;
              const isActive = energy === lvl.value;
              return (
                <button
                  key={lvl.value}
                  type="button"
                  onClick={() => setEnergy(lvl.value)}
                  className={`flex flex-col items-center gap-1 p-2 rounded-lg transition-all ${
                    isActive
                      ? 'bg-white/5 border border-white/10 scale-105'
                      : 'hover:bg-white/5'
                  }`}
                  data-testid={`energy-level-${lvl.value}`}
                >
                  <Icon className={`w-7 h-7 ${isActive ? lvl.color : 'text-zinc-700'}`} />
                  <span className={`text-[9px] font-bold uppercase tracking-wider ${
                    isActive ? lvl.color : 'text-zinc-600'
                  }`}>
                    {fr ? lvl.label_fr : lvl.label_en}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Submit */}
        <div className="px-6 pb-6 pt-2">
          <Button
            onClick={() => onSubmit({ intensity_rating: intensity, energy_level: energy })}
            disabled={!canSubmit}
            className="w-full h-12 font-heading text-sm uppercase tracking-wider"
            data-testid="submit-debrief-btn"
          >
            {fr ? 'Valider le debriefing' : 'Submit debrief'}
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
}
